import { useState } from 'react'
import { Button } from './ui'
import { useToast } from '@/context/ToastContext'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/api'
import type { FillFormKind } from '@/api/types'

/**
 * 「AI 填充」按钮 —— **全站唯一一份实现**。
 *
 * 2026-10-01 抽出。在此之前只有 `/banner` 有这颗按钮，逻辑写在页面里；
 * 用户要求「视频页也加一个，其他页面也加，看看能不能封装一下」。
 *
 * 六个页面（banner / video / social / copy / detail / digital-human）要的
 * **行为完全一样**，只有三处不同：**上下文**（各页字段与平台真源不同）、
 * **能写回哪几个字段**、**什么情况下不让点**。这三样做成 props，其余逻辑收在这里 ——
 * 抄五份的话，「成功给撤销」这类约定一定会有一页漏掉，而漏掉的那页
 * 用户被覆盖了草稿还拿不回来（本项目「交互控件只许一份实现」的复发点）。
 *
 * ## 四条不可省的约定（与 `/prompt/refine` 同族）
 *
 * ① **它不是生成任务**。不建 job、不进任务中心、右栏不会多一张卡 ——
 *    它改的是**输入**（表单字段）。但它真花钱（后端按条 ¥0.10 并落用量账本），
 *    所以失败要如实报、成功要报清花了多少。
 * ①' **成功之后必须刷一次余额**（2026-10-07 补）。这颗按钮是**按次计费**的：
 *    后端 `_bill_text_call` 真的从账号余额里扣了积分。不刷的话顶栏那个数字
 *    一动不动，用户以为没扣钱 —— 而账本上已经记了一笔。
 *    提示语里的额度数用后端回的 `coins`（积分口径），不用 `cost`（元）——
 *    顶栏是积分，提示说元，用户对不上账。
 * ② **只写回 `fields` 白名单里的键**。后端已经用 `_FILL_FIELDS` 过滤过一遍，
 *    这里再挡一道不是不信任它 —— 而是「后端多返回一个键」的代价不对称：
 *    键名对不上时 React 状态里会多出一个没人认识的字段，写进草稿后还会一直跟着。
 * ③ **空结果必须报错**。「花了钱、提示成功、输入框一个字没变」是最坏的失败形态。
 *    后端已经保证过一次（返回空 dict 会 502），这里同样不放过。
 * ④ **覆盖前留快照，成功给「撤销」**。AI 会重写字段（哪怕用户已经写了草稿），
 *    手写过的东西必须能一键拿回来 —— 不给撤销的覆盖，用户下次就不敢点了。
 *    `snapshot` 由调用方在**调接口之前**取好传进来（组件内取就晚了，那时已被覆盖）。
 *
 * ## 为什么「禁用」而不是「隐藏」
 *
 * 前置条件不满足时（比如没填产品名）按钮**留在原位、变灰**，原因写进 `title`。
 * 藏起来的话用户永远不知道这里有捷径；而 AI 猜错一次的成本，
 * 远高于他先补一个产品名。
 */
export function AiFillButton<F extends Record<string, unknown>>({
  kind,
  brief,
  sourcePage,
  fields,
  snapshot,
  onFill,
  blocked,
  label = 'AI 填充',
  busyLabel = 'AI 正在写…',
}: {
  /** 填充场景。决定后端按哪套 system 问、返回哪几个键 */
  kind: FillFormKind
  /** 场景上下文。**由调用方用本页真源拼**（见 `config/prompts.ts` 的 `build*FillBrief`） */
  brief: string
  /** 出处，只用于用量账本 */
  sourcePage?: string
  /** 允许写回的字段（**逐字等于本页表单的键**）。白名单外的返回值一律丢掉 */
  fields: readonly (keyof F & string)[]
  /** 覆盖前的字段快照，供「撤销」用。**必须在调接口之前取** */
  snapshot: Partial<F>
  /** 拿到字段后写回表单（通常就是 `patch`） */
  onFill: (next: Partial<F>) => void
  /** 不让点的原因。有值 = 禁用，值会出现在 hover 提示里 */
  blocked?: string
  label?: string
  busyLabel?: string
}) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const { refresh: refreshBalance } = useAuth()

  const run = async () => {
    if (busy || blocked) return
    setBusy(true)
    try {
      const res = await api.fillForm({ brief, kind, sourcePage })
      /*
       * 拿到响应 = 后端**已经收过费**（`_bill_text_call` 记在解析字段之前，
       * 所以哪怕下面因为字段为空抛错，这笔钱也真的花掉了）。所以刷新要放在这里、
       * 不能放进下面的成功分支 —— 否则「花了钱 + 提示失败 + 余额不动」，
       * 用户会以为既没花钱也没填上，然后再点一次。
       */
      void refreshBalance()
      const next: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(res.fields)) {
        if ((fields as readonly string[]).includes(key)) next[key] = value
      }
      const n = Object.keys(next).length
      if (!n) throw new Error('模型没有返回可用的字段，输入框都没有变化')
      onFill(next as Partial<F>)
      /* 额度数用后端回的 `coins`（积分口径，与顶栏同源），**不写 `cost × 10`** ——
         自己乘就是第二份换算真源，改口径时必漏。 */
      const coinNote = res.coins > 0 ? ` · 扣 ${res.coins} 积分` : ''
      toast.toast(
        `AI 已填入 ${n} 项（${res.model} · ¥${res.cost.toFixed(2)}${coinNote} · ` +
          `${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，不满意就点「撤销」。`,
        'success',
        { label: '撤销', onClick: () => onFill(snapshot) },
      )
    } catch (e) {
      // 如实报错，且把后端/上游的原话带出来：Key 失效、限流、返回不像 JSON
      // 三种原因的处理方式完全不同，吞成「失败了」等于让人去猜。
      toast.error(`AI 填充失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      icon="sparkles"
      loading={busy}
      /* 禁用而不是藏起来：让「这里能一键补齐」一直看得见（见组件头注） */
      disabled={!!blocked}
      title={blocked}
      onClick={run}
    >
      {busy ? busyLabel : label}
    </Button>
  )
}
